import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Scale, Loader2, Save, Pencil, X, Trash2, IndianRupee } from 'lucide-react';

import {
    fetchApprLimitWorkflows,
    fetchApprovalLimitsGrid,
    submitApprovalLimit,
    clearApprovalLimitSaveResult,
    selectWorkflowList,
    selectApprovalLimitsGrid,
    selectApprovalLimitSaveResult,
    selectWorkflowsLoading,
    selectApprovalGridLoading,
    selectApprovalSaveLoading,
    selectApprovalGridError,
    selectApprovalSaveError,
} from '../../slices/accountsSlice/approvalLimitSettingsSlice';
import { showToast } from '../../utilities/toastUtilities';

const currencyFmt = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
});

const ApprovalLimitSettings = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';
    const roleId = userData?.roleId || userData?.RID || 0;

    const workflowList = useSelector(selectWorkflowList);
    const gridList      = useSelector(selectApprovalLimitsGrid);
    const saveResult    = useSelector(selectApprovalLimitSaveResult);

    const workflowsLoading = useSelector(selectWorkflowsLoading);
    const gridLoading      = useSelector(selectApprovalGridLoading);
    const gridError        = useSelector(selectApprovalGridError);
    const saveLoading      = useSelector(selectApprovalSaveLoading);
    const saveError        = useSelector(selectApprovalSaveError);

    const [moId, setMoId] = useState('');
    const [amount, setAmount] = useState('');

    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);

    const load = () => {
        dispatch(fetchApprLimitWorkflows());
        dispatch(fetchApprovalLimitsGrid());
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    useEffect(() => {
        if (saveResult) {
            const messages = { Add: 'Approval limit added successfully.', Update: 'Approval limit updated successfully.', Delete: 'Approval limit deleted successfully.' };
            showToast('success', messages[saveResult.action] || 'Saved successfully.');
            dispatch(clearApprovalLimitSaveResult());
            setMoId('');
            setAmount('');
            setEditingId(null);
            setEditValue('');
            setBusyId(null);
            setPendingAction(null);
            load();
        }
        if (saveError) {
            showToast('error', typeof saveError === 'string' ? saveError : 'Failed to save the approval limit.');
            dispatch(clearApprovalLimitSaveResult());
            setBusyId(null);
            setPendingAction(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, saveError, dispatch]);

    const handleAdd = () => {
        if (!moId) { showToast('error', 'Please select a Work Flow.'); return; }
        const val = parseFloat(amount);
        if (amount === '' || isNaN(val) || val <= 0) { showToast('error', 'Please enter a valid limit amount.'); return; }

        dispatch(submitApprovalLimit({
            LimitId:   0,
            MoId:      Number(moId),
            Amount:    val,
            CreatedBy: currentUser,
            Action:    'Add',
            RoleId:    roleId,
        }));
    };

    const handleStartEdit = (row) => {
        setEditingId(row.LimitId);
        setEditValue(row.Amount ?? '');
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditValue('');
    };

    const handleUpdate = (row) => {
        const val = parseFloat(editValue);
        if (editValue === '' || isNaN(val) || val <= 0) { showToast('error', 'Please enter a valid limit amount.'); return; }
        if (val === parseFloat(row.Amount)) { handleCancelEdit(); return; }

        setBusyId(row.LimitId);
        setPendingAction('Update');
        dispatch(submitApprovalLimit({
            LimitId:   row.LimitId,
            MoId:      row.MoId,
            Amount:    val,
            CreatedBy: currentUser,
            Action:    'Update',
            RoleId:    roleId,
        }));
    };

    const handleDelete = (row) => {
        if (!window.confirm(`Do you want to delete the approval limit settings for work flow: ${row.MasterOperation}?`)) return;

        setBusyId(row.LimitId);
        setPendingAction('Delete');
        dispatch(submitApprovalLimit({
            LimitId:   row.LimitId,
            MoId:      row.MoId,
            Amount:    row.Amount,
            CreatedBy: currentUser,
            Action:    'Delete',
            RoleId:    roleId,
        }));
    };

    const canAdd = moId && amount !== '' && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* Page Header */}
            <div className="max-w-5xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-6 text-white">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20 flex-shrink-0">
                            <Scale className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">
                                Configuration
                            </span>
                            <h1 className="text-xl md:text-2xl font-black tracking-tight mt-1">Approval Limit Settings</h1>
                            <p className="text-indigo-200 text-sm mt-0.5">
                                Set the amount above which the second-to-last approval level is required for a work flow.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto space-y-4">

                {/* Add form */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div className="md:col-span-2">
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                Work Flow
                            </label>
                            <select
                                value={moId}
                                onChange={(e) => setMoId(e.target.value)}
                                disabled={workflowsLoading}
                                className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60"
                            >
                                <option value="">-Please Select-</option>
                                {workflowList.map((w) => (
                                    <option key={w.MOID} value={w.MOID}>{w.Name}</option>
                                ))}
                            </select>
                            {!workflowsLoading && workflowList.length === 0 && (
                                <p className="text-[11px] text-gray-400 mt-1">
                                    All eligible work flows already have a limit configured.
                                </p>
                            )}
                        </div>

                        <div className="flex items-end gap-2">
                            <div className="flex-1">
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    Limit Amount
                                </label>
                                <div className="relative">
                                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                                        placeholder="0.00"
                                        className="w-full pl-8 pr-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={handleAdd}
                                disabled={!canAdd || saveLoading}
                                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {saveLoading && !editingId && !busyId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                Submit
                            </button>
                        </div>
                    </div>
                </div>

                {/* Grid */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {gridLoading ? (
                        <div className="py-16 flex flex-col items-center justify-center gap-3">
                            <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
                            <span className="text-sm text-gray-400">Loading approval limits…</span>
                        </div>
                    ) : gridError ? (
                        <div className="py-10 text-center text-sm text-rose-500">{gridError}</div>
                    ) : gridList.length === 0 ? (
                        <div className="py-16 text-center text-gray-400 dark:text-gray-500 text-sm">
                            No approval limits configured yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Work Flow</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Role</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Amount</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {gridList.map((row) => {
                                        const isEditing = editingId === row.LimitId;
                                        const isBusy = busyId === row.LimitId && saveLoading;
                                        return (
                                            <tr key={row.LimitId} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                                <td className="px-4 py-3 font-semibold text-gray-800 dark:text-gray-100">{row.MasterOperation}</td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{row.RoleName}</td>
                                                <td className="px-4 py-3 text-right">
                                                    {isEditing ? (
                                                        <div className="relative w-40 ml-auto">
                                                            <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                                                            <input
                                                                type="text"
                                                                inputMode="decimal"
                                                                autoFocus
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleUpdate(row);
                                                                    if (e.key === 'Escape') handleCancelEdit();
                                                                }}
                                                                disabled={isBusy}
                                                                className="w-full pl-7 pr-3 py-1.5 rounded-lg border-2 text-sm text-right font-semibold bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-indigo-300 dark:border-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 disabled:opacity-60"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <span className="font-semibold text-indigo-700 dark:text-indigo-400">
                                                            {currencyFmt.format(row.Amount)}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {isEditing ? (
                                                        <div className="flex items-center gap-1.5 justify-end">
                                                            <button
                                                                onClick={() => handleUpdate(row)}
                                                                disabled={isBusy}
                                                                title="Save"
                                                                className="p-1.5 rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 transition-colors"
                                                            >
                                                                {isBusy && pendingAction === 'Update' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                                            </button>
                                                            <button
                                                                onClick={handleCancelEdit}
                                                                disabled={isBusy}
                                                                title="Cancel"
                                                                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60 transition-colors"
                                                            >
                                                                <X className="h-3.5 w-3.5" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-1.5 justify-end">
                                                            <button
                                                                onClick={() => handleStartEdit(row)}
                                                                disabled={isBusy}
                                                                title="Edit limit"
                                                                className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 disabled:opacity-60 transition-colors"
                                                            >
                                                                <Pencil className="h-3.5 w-3.5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(row)}
                                                                disabled={isBusy}
                                                                title="Delete"
                                                                className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-60 transition-colors"
                                                            >
                                                                {isBusy && pendingAction === 'Delete' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApprovalLimitSettings;
